import { AiOutlineInbox, AiOutlineLineChart } from "react-icons/ai";
import { isKnownSettingsTarget } from "@/components/settings-patch-review/settings-target-query-keys";
import { ANALYTICS_ROOT, ANALYTICS_SECTION_PERMISSIONS, SCHEDULED_REPORTS_PERMISSIONS, analyticsDashboardMeta, getAnalyticsDashboardMeta, groupAnalyticsDashboards } from "@/config/analytics-nav";
import { routeModulesEnabled } from "@/lib/module-gate";
import { PERMISSIONS } from "@/lib/permissions";
import { tenantMatchesAllowedSubDomains } from "@/lib/tenant-flags";
import { isSandboxSubdomain } from "@/lib/tenant-utils";
import { RoleName } from "@/types/provider";
import { Module, Tenant } from "@/types/tenant";
export enum DynamicNameKey {
  PATIENT = "patient",
  CARE_PLAN = "care-plan",
  CARE_PLAN_TEMPLATE = "care-plan-template",
  NOTE = "note",
  LOCATION = "location",
}
export interface CurrentSettingsResourceIdentity {
  targetId: string;
  recordId: string;
  mode?: "view" | "edit" | "usage" | "members";
}
export interface SiteMapItem {
  name: string;
  route?: string;
  moduleName?: Module;
  /** Show this route if the tenant has **any** of these modules (OR logic) */
  moduleNames?: Module[];
  /** Gates this item's own page (any of) without hiding its children. */
  routeModuleNames?: Module[];
  description?: string;
  keywords?: string[];
  children?: SiteMapItem[];
  alertCount?: number;
  hidden?: boolean;
  suggested?: boolean;
  roles?: RoleName[];
  permissions?: string[];
  /** Also required, every one of them, on top of `permissions`. */
  allPermissions?: string[];
  icon?: React.ReactNode;
  showNewBadge?: boolean;
  /** Special action identifier for items that don't navigate but trigger actions */
  action?: "notifications" | "patient-status" | "tasks";
  /** If set, only show this item for tenants whose subDomain includes one of these strings */
  tenantSubDomains?: string[];
  /** Only show this item in sandbox clinics (see `isSandboxSubdomain`). */
  sandboxOnly?: boolean;
  /** Key for dynamic name resolution. When set, the name will be resolved dynamically based on route params */
  dynamicNameKey?: DynamicNameKey;
  /** Identifies a settings-engine record represented by a dynamic route. */
  settingsResourceContext?: {
    targetId: string;
    recordIdParam: string;
    mode?: CurrentSettingsResourceIdentity["mode"];
  };
  /** Render this crumb as a muted, non-emphasized context line rather than the bold last crumb. */
  breadcrumbMuted?: boolean;
  /** A trailing, more-muted detail shown after (and separated from) this crumb's name — e.g. a note's visit date. */
  breadcrumbMeta?: string;
  /**
   * Extra routes that should resolve to this entry for breadcrumb, module
   * lookup, and active-state highlighting. Lets one nav entry cover sibling
   * routes that share the same conceptual destination (e.g. a v2 page).
   */
  routeAliases?: string[];
  /** Shared destination used by a temporary proxy route. */
  canonicalRoute?: string;
  /** Describes how a visible Settings destination reaches its implementation. */
  destinationKind?: "canonical" | "contextual-alias" | "external-proxy";
}
export const providerSiteMap: SiteMapItem[] = [{
  name: "Notifications",
  description: "View and manage your notifications",
  keywords: ["notifications", "alerts", "inbox"],
  action: "notifications"
}, {
  name: "Tasks",
  route: "/tasks",
  description: "Manage and track tasks",
  keywords: ["tasks", "todo", "workflow", "reminders", "assignments"],
  suggested: true,
  permissions: [PERMISSIONS.TASKS_READ],
  action: "tasks"
}, {
  name: "Ask Decoda",
  route: "/ask-decoda",
  description: "Chat with the Decoda AI assistant in full screen",
  keywords: ["AI", "assistant", "Ask Decoda", "help", "chat"],
  suggested: true,
  moduleName: "ASK_DECODA",
  permissions: [PERMISSIONS.AI_USE]
}, {
  name: "Check-In",
  description: "Manage patient check-in process",
  route: "/check-in/dashboard",
  keywords: ["check-in", "dashboard", "appointments", "scheduling"],
  icon: <AiOutlineLineChart size={18} />,
  permissions: [PERMISSIONS.SCHEDULING_READ],
  routeModuleNames: getAnalyticsDashboardMeta("check-in")?.requiredModules,
  children: [{
    name: "Patient Status",
    description: "View patient check-in and waiting status",
    keywords: ["patient status", "waiting room", "check-in", "waiting"],
    action: "patient-status",
    moduleName: "CALENDAR_PATIENT_STATUS",
    permissions: [PERMISSIONS.SCHEDULING_READ]
  }, {
    name: "Calendar",
    moduleName: "SCHEDULING",
    route: "/check-in/calendar",
    keywords: ["calendar", "schedule", "appointments"],
    description: "Manage appointments",
    permissions: [PERMISSIONS.SCHEDULING_READ]
  }, {
    name: "Shift Management",
    moduleName: "SCHEDULING",
    route: "/settings/shifts",
    hidden: true,
    keywords: ["shift", "management", "scheduling", "shifts"],
    description: "Manage shifts",
    permissions: [PERMISSIONS.SCHEDULING_SHIFTS_WRITE]
  }, {
    name: "Clock In / Out",
    moduleName: "CLOCK_IN",
    route: "/check-in/clock-in-history",
    keywords: ["clock in", "clock out", "time entry", "history", "check-in"],
    description: "View clock-in and clock-out history",
    permissions: [PERMISSIONS.CLOCK_IN_HISTORY_READ, PERMISSIONS.CLOCK_IN_HISTORY_READ_ALL]
  }, {
    name: "Create Appointment",
    moduleName: "SCHEDULING",
    route: "/check-in/appointment-form/new",
    keywords: ["create", "appointment", "scheduling", "booking", "new"],
    description: "Create a new appointment",
    hidden: true
  }, {
    name: "Edit Appointment",
    moduleName: "SCHEDULING",
    route: "/check-in/appointment-form/:id",
    keywords: ["edit", "appointment", "scheduling", "booking", "update"],
    description: "Edit an existing appointment",
    hidden: true
  }, {
    name: "Form Submissions",
    moduleName: "FORMS",
    route: "/check-in/forms/submissions",
    keywords: ["forms", "submissions", "intake"],
    description: "View form submissions",
    permissions: [PERMISSIONS.FORMS_SUBMISSIONS_READ]
  }]
}, {
  name: "Communications",
  description: "Manage all patient communications",
  children: [{
    name: "Chat",
    route: "/comms/chat",
    description: "Manage patient conversations",
    moduleName: "TEXTING",
    keywords: ["chat", "messaging", "conversations", "messages", "text"],
    suggested: true,
    permissions: [PERMISSIONS.MESSAGING_CHATS_READ]
  }, {
    name: "Email",
    route: "/comms/email",
    description: "Manage clinic email threads",
    moduleName: "EMAILS",
    keywords: ["email", "mail", "inbox", "threads", "messages"],
    suggested: true,
    permissions: [PERMISSIONS.MESSAGING_EMAILS_READ]
  }, {
    name: "Calls",
    route: "/comms/call",
    description: "Manage voice calls",
    moduleName: "CALLING",
    keywords: ["calls", "voice", "phone", "telephone", "voip"],
    suggested: true,
    permissions: [PERMISSIONS.MESSAGING_CALLS_READ]
  }, {
    name: "Social",
    route: "/comms/social",
    description: "Manage Instagram and Messenger conversations",
    moduleName: "SOCIALS",
    keywords: ["social", "instagram", "facebook", "messenger", "dm"],
    permissions: [PERMISSIONS.MESSAGING_SOCIAL_READ]
  }, {
    name: "Blasts",
    route: "/comms/blasts",
    description: "Send bulk SMS and email to patients",
    moduleNames: ["TEXTING", "EMAILS"],
    keywords: ["blasts", "bulk", "batch", "mass", "broadcast", "sms", "email", "campaign"],
    permissions: [PERMISSIONS.MESSAGING_BLASTS_READ, PERMISSIONS.MESSAGING_BLASTS_WRITE],
    children: [{
      name: "New Blast",
      route: "/comms/blasts/new",
      hidden: true,
      permissions: [PERMISSIONS.MESSAGING_BLASTS_WRITE]
    }, {
      name: "Blast Detail",
      route: "/comms/blasts/:blastId",
      hidden: true
    }]
  }, {
    name: "Sequences",
    route: "/comms/sequences",
    description: "Automated outreach sequences for patient engagement",
    moduleNames: ["OUTREACH"],
    keywords: ["sequences", "outreach", "automation", "campaigns", "drip", "engagement"],
    suggested: true,
    permissions: [PERMISSIONS.MESSAGING_SEQUENCES_READ, PERMISSIONS.MESSAGING_SEQUENCES_WRITE],
    children: [{
      name: "New Sequence",
      route: "/comms/sequences/new",
      hidden: true,
      permissions: [PERMISSIONS.MESSAGING_SEQUENCES_WRITE]
    }, {
      name: "Sequence Detail",
      route: "/comms/sequences/:sequenceId",
      settingsResourceContext: {
        targetId: "tenant.sequences",
        recordIdParam: "sequenceId",
        mode: "view"
      },
      hidden: true
    }]
  }, {
    name: "Fax",
    route: "/comms/fax",
    description: "Send and manage faxes",
    keywords: ["fax", "faxes", "send", "documents"],
    moduleName: "FAX",
    // Page-level gate is read-only — sending a fax is gated separately on
    // MESSAGING_FAX_WRITE in the compose dialog / send endpoint, so
    // view-only roles can still inspect inbound/outbound history.
    permissions: [PERMISSIONS.MESSAGING_FAX_READ],
    children: [{
      name: "Contacts",
      route: "/comms/fax/contacts",
      description: "Manage fax recipients",
      moduleName: "FAX",
      permissions: [PERMISSIONS.MESSAGING_FAX_READ]
    }]
  }]
}, {
  name: "Billing",
  keywords: ["billing", "payments", "invoices", "charges", "dashboard"],
  route: "/billing/dashboard",
  description: "Get an overview of your billing",
  icon: <AiOutlineLineChart size={18} />,
  permissions: [PERMISSIONS.BILLING_REPORTS_READ],
  routeModuleNames: getAnalyticsDashboardMeta("billing")?.requiredModules,
  children: [{
    name: "Payment Check-out",
    route: "/billing/check-out",
    moduleName: "PAYMENTS",
    keywords: ["billing", "payment", "checkout", "check out"],
    description: "Check-out patients and process payments",
    permissions: [PERMISSIONS.BILLING_PAYMENTS_PROCESS]
  }, {
    name: "Charges",
    route: "/billing/charges",
    moduleName: "PAYMENTS",
    keywords: ["billing", "charges", "payments"],
    description: "View and manage charges",
    suggested: true,
    permissions: [PERMISSIONS.BILLING_READ]
  }, {
    name: "Payments",
    route: "/billing/payments",
    moduleName: "PAYMENTS",
    keywords: ["billing", "payments"],
    description: "View and manage payments",
    permissions: [PERMISSIONS.BILLING_READ]
  }, {
    name: "Deposits",
    route: "/billing/deposits",
    moduleName: "PAYMENTS",
    keywords: ["billing", "deposits", "settlements", "payouts"],
    description: "View and manage deposits",
    permissions: [PERMISSIONS.BILLING_DEPOSITS_READ],
    hidden: true
  }, {
    name: "Memberships",
    route: "/billing/memberships",
    keywords: ["memberships", "members", "plans", "subscriptions", "recurring", "past due", "delinquent", "declined", "collections", "failed payments"],
    description: "Membership plans, members and past-due payments",
    permissions: [PERMISSIONS.MEMBERSHIPS_READ]
  }, {
    name: "Membership Plan",
    route: "/billing/memberships/:membershipId",
    settingsResourceContext: {
      targetId: "tenant.memberships",
      recordIdParam: "membershipId",
      mode: "members"
    },
    keywords: ["membership", "plan", "members"],
    description: "One membership plan's members",
    permissions: [PERMISSIONS.MEMBERSHIPS_READ],
    hidden: true
  }, {
    name: "Cash Balances",
    route: "/billing/cash-balances",
    moduleName: "CASH_BALANCES",
    keywords: ["billing", "cash", "balances", "reconciliation", "drawer"],
    description: "Manage cash drawer balances and reconciliation",
    permissions: [PERMISSIONS.BILLING_READ]
  }, {
    name: "Staff Pay",
    route: "/billing/staff-pay",
    moduleNames: ["PAYMENTS", "COMMISSIONS", "CLOCK_IN"],
    keywords: ["commissions", "payouts", "reports", "staff pay", "tips", "hours"],
    description: "View commissions, tips, and hours worked",
    permissions: [PERMISSIONS.BILLING_COMMISSIONS_READ]
  }, {
    name: "Close Register",
    route: "/billing/cash-balances/close-register",
    moduleName: "CASH_BALANCES",
    keywords: ["billing", "cash", "drawer", "reconciliation", "close register"],
    description: "Close and reconcile cash register",
    hidden: true
  }, {
    name: "Invoices",
    route: "/billing/invoices",
    moduleName: "PAYMENTS",
    keywords: ["billing", "invoice", "invoicing", "invoices"],
    description: "Manage invoices",
    permissions: [PERMISSIONS.BILLING_INVOICES_READ]
  }, {
    name: "Quotes",
    route: "/billing/carts",
    moduleName: "QUOTES",
    keywords: ["quotes", "quote", "estimate", "billing", "draft"],
    description: "Manage patient quotes",
    permissions: [PERMISSIONS.BILLING_READ]
  }, {
    name: "Online Orders",
    route: "/billing/online-orders",
    moduleName: "SHOP",
    keywords: ["online orders", "shop orders", "online shop", "fulfillment", "shipping", "pickup", "packing"],
    description: "Ship or hand over online shop purchases",
    permissions: [PERMISSIONS.BILLING_READ]
  }, {
    name: "Create Quote",
    route: "/billing/check-out",
    moduleName: "QUOTES",
    keywords: ["create quote", "quote", "quotes", "estimate", "draft"],
    description: "Create a new quote",
    permissions: [PERMISSIONS.BILLING_PAYMENTS_PROCESS],
    hidden: true
  }, {
    name: "Chargebacks",
    route: "/billing/chargebacks",
    moduleName: "PAYMENTS",
    keywords: ["billing", "chargebacks", "disputes", "chargeback"],
    description: "View and manage chargebacks and disputes",
    permissions: [PERMISSIONS.BILLING_CHARGEBACKS_READ]
  }, {
    name: "QuickBooks",
    route: "/billing/quickbooks",
    moduleNames: ["QUICKBOOKS"],
    keywords: ["quickbooks", "close", "journal", "post", "books", "reconcile"],
    description: "Close periods and post them to QuickBooks Online",
    permissions: [PERMISSIONS.QUICKBOOKS_MANAGE]
  }, {
    name: "Tip Screen",
    route: "/tip-screen",
    moduleName: "PAYMENTS",
    keywords: ["tip", "tips", "tipping", "tip screen", "tip display", "customer display", "customer facing", "gratuity"],
    description: "Customer-facing tip display screen",
    hidden: true
  }]
}, {
  name: "Clinical",
  description: "Clinical documentation, attachments, and prescriptions",
  keywords: ["clinical", "notes", "gallery", "documentation", "AI", "prescriptions"],
  children: [{
    name: "Notes",
    route: "/scribe",
    moduleName: "SCRIBE",
    description: "AI-powered clinical documentation",
    keywords: ["scribe", "notes", "documentation", "AI"],
    permissions: [PERMISSIONS.CLINICAL_NOTES_READ],
    // Note Editor is nested here (not a sibling) so the breadcrumb reads
    // "Notes › {patient} - {title} {date}". It stays a plain link in the
    // sidebar: the only child is hidden and its ":id" route never matches
    // the concrete path, so the accordion never expands.
    children: [{
      name: "Note Editor",
      route: "/scribe/playground/:id",
      hidden: true,
      description: "Generate clinical notes with AI assistance",
      keywords: ["dictation", "transcription", "voice recording", "note taking"],
      permissions: [PERMISSIONS.CLINICAL_NOTES_READ],
      // The note's patient/title/visit-date replaces the crumb text and
      // is shown muted so it reads as persistent chart context.
      dynamicNameKey: DynamicNameKey.NOTE,
      breadcrumbMuted: true
    }]
  }, {
    name: "Labs",
    route: "/labs",
    // OR, mirroring the backend's RequireAnyLabsModule — a tenant holding only
    // one of the lab modules still gets the page.
    moduleNames: ["LABS", "FULLSCRIPT_LABS", "ACCESS_LABS", "UPLOADED_LABS", "IN_HOUSE_LABS"],
    description: "Every lab order across patients, in one place",
    keywords: ["labs", "lab orders", "lab results", "junction", "fullscript", "access", "uploaded", "in-house"],
    permissions: [PERMISSIONS.LABS_READ]
  }, {
    name: "Lab Order",
    route: "/labs/:orderId",
    hidden: true,
    moduleNames: ["LABS", "FULLSCRIPT_LABS", "ACCESS_LABS", "UPLOADED_LABS", "IN_HOUSE_LABS"],
    description: "View a single lab order's details and results",
    keywords: ["lab order", "lab results"],
    permissions: [PERMISSIONS.LABS_READ]
  }, {
    name: "Gallery",
    route: "/scribe/gallery",
    // TODO: Currently only hidden from sidebar. When Rivas tenant is split
    // into wellness vs weightloss, complete the full feature flag (API routes,
    // patient sidebar, gallery picker modal, gallery page) so the weightloss
    // tenant doesn't have gallery at all.
    moduleName: "GALLERY",
    description: "View all medical attachments",
    keywords: ["attachments", "images", "gallery", "photos"],
    suggested: true,
    permissions: [PERMISSIONS.CLINICAL_GALLERY_READ]
  }]
}, {
  name: "Inventory",
  description: "Manage your inventory",
  keywords: ["inventory", "items", "stocks", "suppliers"],
  suggested: false,
  children: [{
    name: "Stock",
    route: "/settings/stock",
    moduleName: "INVENTORY",
    hidden: true,
    description: "Manage stock levels and suppliers",
    keywords: ["stock", "suppliers", "inventory"],
    suggested: true,
    permissions: [PERMISSIONS.INVENTORY_READ]
  }, {
    name: "Purchase Orders",
    route: "/inventory/purchase-orders",
    moduleName: "INVENTORY",
    description: "Manage purchase orders and shipments",
    keywords: ["purchase orders", "purchase order", "PO", "orders", "suppliers", "inventory"],
    suggested: true,
    permissions: [PERMISSIONS.INVENTORY_READ]
  }, {
    name: "Requisitions",
    route: "/inventory/requisitions",
    moduleName: "COUPA",
    description: "Requests sent to Coupa for approval",
    keywords: ["requisitions", "requisition", "approval", "coupa", "punch-out", "punchout", "inventory"],
    permissions: [PERMISSIONS.INVENTORY_READ]
  }, {
    name: "Suppliers",
    route: "/inventory/suppliers",
    moduleName: "INVENTORY",
    description: "Manage suppliers and their contact information",
    keywords: ["suppliers", "vendor", "vendors", "inventory"],
    suggested: true,
    permissions: [PERMISSIONS.INVENTORY_READ]
  }, {
    name: "Add Stock",
    route: "/settings/stock/add",
    moduleName: "INVENTORY",
    description: "Add new stock items",
    keywords: ["add stock", "stock", "new", "inventory", "create"],
    hidden: true,
    // This page creates and edits stock (?id=), both of which require
    // inventory:write. The route guard blocks direct navigation without it.
    permissions: [PERMISSIONS.INVENTORY_WRITE]
  }]
}, {
  name: "Growth",
  route: "/growth/dashboard",
  description: "Track and convert prospective patients",
  moduleName: "MARKETING",
  permissions: [PERMISSIONS.GROWTH_READ],
  icon: <AiOutlineLineChart size={18} />,
  keywords: ["leads", "prospects", "crm", "pipeline", "campaigns", "growth", "dashboard"],
  children: [{
    name: "Campaigns",
    route: "/growth/campaigns",
    keywords: ["campaigns", "growth", "ads", "social"],
    description: "Manage growth campaigns",
    permissions: [PERMISSIONS.GROWTH_READ],
    children: [{
      name: "New Campaign",
      route: "/growth/campaigns/new",
      hidden: true
    }]
  }, {
    name: "Leads",
    route: "/growth/leads",
    keywords: ["leads", "opportunities", "prospects", "pipeline", "kanban", "table", "attribution"],
    description: "Manage leads in table or Kanban views",
    permissions: [PERMISSIONS.GROWTH_READ]
  }, {
    name: "Market Map",
    route: "/growth/market-map",
    moduleName: "MARKET_MAP",
    keywords: ["market map", "zip codes", "map", "heat map", "demographics", "census", "where patients live", "marketing spend", "territory"],
    description: "See where patients live and which ZIP codes to market to next",
    permissions: [PERMISSIONS.GROWTH_READ]
  }, {
    name: "Lead Forms",
    route: "/growth/lead-forms",
    keywords: ["lead forms", "forms", "website form", "embed", "qr code", "inquiries"],
    description: "Build public forms that capture new leads",
    // The forms API gates these, same as the clinical forms list; the
    // campaign picker needs Growth read.
    permissions: [PERMISSIONS.FORMS_TEMPLATES_READ, PERMISSIONS.FORMS_TEMPLATES_WRITE],
    allPermissions: [PERMISSIONS.GROWTH_READ],
    children: [{
      name: "New Lead Form",
      route: "/growth/lead-forms/new",
      hidden: true,
      permissions: [PERMISSIONS.FORMS_TEMPLATES_WRITE],
      allPermissions: [PERMISSIONS.GROWTH_READ]
    }, {
      name: "Edit Lead Form",
      route: "/growth/lead-forms/:formId",
      settingsResourceContext: {
        targetId: "tenant.forms",
        recordIdParam: "formId",
        mode: "edit"
      },
      hidden: true,
      permissions: [PERMISSIONS.FORMS_TEMPLATES_WRITE],
      allPermissions: [PERMISSIONS.GROWTH_READ]
    }]
  }, {
    name: "Pipelines",
    route: "/settings/pipelines",
    moduleName: "MARKETING",
    hidden: true,
    keywords: ["pipelines", "leads", "opportunities", "stages", "funnel", "growth", "attribution"],
    description: "Configure lead pipelines and stages",
    children: [{
      name: "New Pipeline",
      route: "/settings/pipelines/new",
      moduleName: "MARKETING",
      hidden: true
    }, {
      name: "Edit Pipeline",
      route: "/settings/pipelines/:id",
      settingsResourceContext: {
        targetId: "tenant.pipelines",
        recordIdParam: "id",
        mode: "edit"
      },
      moduleName: "MARKETING",
      hidden: true
    }]
  }, {
    name: "Integrations",
    route: "/settings/integrations",
    moduleName: "MARKETING",
    hidden: true,
    keywords: ["integrations", "connect", "meta", "facebook", "instagram", "google", "ads", "oauth"],
    description: "Connect Meta Ads, Google Ads, and other platforms"
  }, {
    name: "AI Agents",
    route: "/settings/ai-agents",
    moduleName: "MARKETING",
    hidden: true,
    description: "Configure AI-powered growth agents",
    keywords: ["agents", "ai", "analysis", "generation", "builder", "automation", "growth agents"],
    children: [{
      name: "New Agent",
      route: "/settings/ai-agents/new",
      moduleName: "MARKETING",
      hidden: true
    }, {
      name: "Edit Agent",
      route: "/settings/ai-agents/:agentId",
      moduleName: "MARKETING",
      hidden: true
    }, {
      name: "Report",
      route: "/settings/ai-agents/reports/:reportId",
      moduleName: "MARKETING",
      hidden: true
    }]
  }, {
    name: "Reviews",
    route: "/reviews",
    description: "View patient reviews and configure where they're collected",
    keywords: ["reviews", "ratings", "feedback", "stars", "google", "yelp", "reputation"],
    suggested: true,
    permissions: [PERMISSIONS.REVIEWS_READ]
  }]
}, {
  name: "Patients",
  route: "/patient",
  description: "Manage patient records",
  keywords: ["patients", "records", "profiles"],
  permissions: [PERMISSIONS.PATIENTS_READ],
  children: [{
    name: "Patients",
    route: "/patient",
    moduleNames: ["TEXTING", "EMAILS"],
    permissions: [PERMISSIONS.MESSAGING_BLASTS_WRITE]
  }, {
    name: "Cohorts",
    route: "/patient/cohorts",
    description: "Reusable patient segments for blasts, sequences, and promotions",
    keywords: ["cohorts", "segments", "audience", "filters"],
    moduleNames: ["TEXTING", "EMAILS"],
    permissions: [PERMISSIONS.MESSAGING_BLASTS_WRITE],
    children: [{
      name: "Cohort Detail",
      route: "/patient/cohorts/:cohortId",
      settingsResourceContext: {
        targetId: "tenant.cohorts",
        recordIdParam: "cohortId",
        mode: "view"
      },
      hidden: true
    }]
  }, {
    name: "Upload",
    route: "/patient/upload",
    description: "Upload patient records",
    keywords: ["patients", "upload", "records"],
    hidden: true
  }, {
    name: "Upload History",
    route: "/patient/upload-history",
    description: "View patient upload history",
    keywords: ["patients", "upload", "history", "records"],
    hidden: true
  }, {
    name: "Merge Patients",
    route: "/patient/merge",
    description: "Merge patient records",
    keywords: ["patients", "merge", "records"],
    hidden: true
  }, {
    name: "Patient Chart",
    route: "/patient/:id",
    description: "View patient chart",
    keywords: ["patients", "chart", "records"],
    hidden: true,
    dynamicNameKey: DynamicNameKey.PATIENT,
    children: [{
      name: "Care Plans",
      route: "/patient/:id/care-plan/:carePlanId",
      description: "View a patient's care plan",
      keywords: ["patients", "care plan", "records"],
      hidden: true,
      dynamicNameKey: DynamicNameKey.CARE_PLAN
    }, {
      name: "New Care Plan",
      route: "/patient/:id/care-plan/new/:templateId",
      description: "Create a new care plan for a patient from template",
      keywords: ["patients", "care plan", "new", "create", "template"],
      hidden: true,
      dynamicNameKey: DynamicNameKey.CARE_PLAN_TEMPLATE
    }]
  }]
}, {
  name: "Analytics",
  // On its way out of the main nav: it lands on the Analytics home page and
  // announces where Analytics lives now. The command bar keeps one row for
  // this destination rather than two, via the canonical route below.
  // Spelled out rather than built from ANALYTICS_ROOT: the page-map generator
  // reads these statically and drops a route it cannot resolve to a literal.
  route: "/analytics?from=nav",
  canonicalRoute: "/analytics",
  destinationKind: "contextual-alias",
  description: "Dashboards and reports across the clinic",
  keywords: ["analytics", "dashboards", "reports", "metrics", "insights"],
  permissions: ANALYTICS_SECTION_PERMISSIONS
}, {
  name: "Wiki",
  route: "/wiki",
  moduleName: "WIKI",
  description: "Clinic knowledge base and documentation",
  keywords: ["wiki", "knowledge", "documentation", "articles", "protocols", "policies"],
  permissions: [PERMISSIONS.WIKI_READ],
  children: [{
    name: "Articles",
    route: "/wiki/articles",
    description: "Browse all wiki articles",
    moduleName: "WIKI",
    permissions: [PERMISSIONS.WIKI_READ]
  }, {
    name: "Tags",
    route: "/wiki/tags",
    description: "Manage wiki tags",
    moduleName: "WIKI",
    permissions: [PERMISSIONS.WIKI_MANAGE_TAGS]
  }, {
    name: "Create Article",
    route: "/wiki/create",
    description: "Create a new wiki article",
    hidden: true,
    moduleName: "WIKI",
    permissions: [PERMISSIONS.WIKI_WRITE]
  }, {
    name: "Edit Article",
    route: "/wiki/:id/edit",
    description: "Edit wiki article",
    hidden: true,
    moduleName: "WIKI",
    permissions: [PERMISSIONS.WIKI_WRITE]
  }, {
    name: "View Article",
    route: "/wiki/:id",
    description: "View wiki article",
    hidden: true,
    moduleName: "WIKI",
    permissions: [PERMISSIONS.WIKI_READ]
  }]
}, {
  name: "Usage",
  route: "/usage",
  description: "View your usage",
  keywords: ["usage"],
  hidden: true,
  permissions: [PERMISSIONS.USAGE_READ]
}, {
  name: "Training",
  route: "/training",
  description: "Practice tasks for your role in this sandbox clinic",
  keywords: ["training", "onboarding", "learn", "practice", "tutorial"],
  hidden: true,
  sandboxOnly: true
}];
const legacySettingsConfig: SiteMapItem[] = [{
  name: "Settings",
  route: "/settings",
  description: "Browse and search all settings",
  keywords: ["settings", "configuration", "preferences", "administration"],
  children: [{
    name: "Personal",
    description: "Personal settings and security",
    children: [{
      name: "Profile",
      route: "/settings/profile",
      description: "Manage your profile information",
      keywords: ["profile", "personal information", "provider details", "bio", "security", "password", "authentication"]
    }, {
      name: "Preferences",
      route: "/settings/preferences",
      description: "Manage your account preferences",
      keywords: ["preferences", "theme", "themes", "light", "dark", "color", "customization", "appearance"]
    }, {
      name: "Notifications",
      route: "/settings/notifications",
      description: "Manage your notification preferences",
      keywords: ["notifications", "alerts", "email", "sms", "preferences"]
    }, {
      name: "Google Calendar",
      route: "/settings/google-calendar",
      description: "Show your personal Google Calendar events on your Decoda schedule and control invite delivery",
      keywords: ["google", "calendar", "gcal", "sync", "block", "invites", "auto-sync"]
    }, {
      name: "Beta Features",
      route: "/settings/beta-features",
      description: "Try new features early and switch back anytime",
      keywords: ["beta", "features", "early access", "experimental", "labs", "dose log", "dose table"]
    }, {
      name: "Lab Panels",
      route: "/settings/lab-panels",
      description: "Build custom lab panels your team can order",
      // Junction and Access — Fullscript labs have no panel catalog of their own.
      moduleNames: ["LABS", "ACCESS_LABS"],
      permissions: [PERMISSIONS.LABS_READ],
      keywords: ["lab panels", "panels", "biomarkers", "markers", "lab tests", "junction", "access", "custom panel"]
    }, {
      name: "Fullscript",
      route: "/settings/fullscript",
      description: "Connect your own Fullscript account",
      // Either product: one account serves both, and this page is where it gets connected.
      // `moduleNames` is OR, so a clinic that only bought labs still gets here.
      moduleNames: ["FULLSCRIPT_SUPPLEMENTS", "FULLSCRIPT_LABS"],
      keywords: ["fullscript", "supplements", "labs", "integration", "oauth", "account"]
    }, {
      name: "Coupa sign-in",
      route: "/settings/coupa-sign-in",
      description: "Sign in to Coupa so your stock changes show as yours",
      moduleName: "COUPA",
      keywords: ["coupa", "sign in", "login", "procurement", "inventory"]
    }]
  }, {
    name: "Organization",
    description: "Organization-wide settings and management",
    children: [{
      name: "Roles",
      route: "/settings/roles",
      description: "Manage roles and permissions",
      keywords: ["roles", "tags", "permissions", "access control", "security", "rbac"],
      permissions: [PERMISSIONS.SETTINGS_ROLES_READ]
    }, {
      name: "Users",
      route: "/settings/users",
      description: "Manage users and assign roles",
      keywords: ["users", "team", "providers", "assign roles"],
      permissions: [PERMISSIONS.USERS_READ, PERMISSIONS.USERS_WRITE]
    }, {
      name: "E-Prescribing",
      route: "/settings/e-prescribing",
      description: "Manage DoseSpot e-prescribing enrollment for providers",
      keywords: ["dosespot", "e-prescribing", "eprescribing", "prescribing", "clinician", "erx"],
      moduleName: "DOSESPOT",
      permissions: [PERMISSIONS.USERS_WRITE, PERMISSIONS.CLINICAL_EPRESCRIBING_ENROL_SELF]
    }, {
      name: "Prescription Presets",
      route: "/settings/prescription-presets",
      description: "Saved prescriptions prescribers can pick from when they e-prescribe",
      keywords: ["dosespot", "presets", "favorites", "clinic favorites", "prescriptions", "e-prescribing", "erx"],
      moduleName: "DOSESPOT",
      permissions: [PERMISSIONS.SETTINGS_READ, PERMISSIONS.CLINICAL_EPRESCRIBING_PRESETS_WRITE]
    }, {
      name: "Calendar Appearance",
      route: "/settings/calendar-appearance",
      description: "Customize how your calendar looks",
      keywords: ["calendar", "appearance", "scheduling", "appointments", "colors", "styles", "icon"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Suggestions",
      route: "/settings/suggestions",
      description: "Configure Suggestions settings",
      keywords: ["assistant", "AI", "automation", "virtual assistant"],
      permissions: [PERMISSIONS.SETTINGS_SUGGESTIONS_READ]
    }, {
      name: "Note Templates",
      route: "/settings/note-templates",
      description: "Manage clinical note templates",
      keywords: ["notes", "templates", "documentation", "clinical"],
      permissions: [PERMISSIONS.SETTINGS_NOTE_TEMPLATES_READ],
      children: [{
        name: "Create",
        route: "/settings/note-templates/new",
        keywords: ["create note template", "new note template"],
        description: "Create a new clinical note template",
        hidden: true,
        permissions: [PERMISSIONS.SETTINGS_NOTE_TEMPLATES_WRITE]
      }, {
        name: "Edit Template",
        route: "/settings/note-templates/:id",
        settingsResourceContext: {
          targetId: "tenant.noteTemplates",
          recordIdParam: "id",
          mode: "edit"
        }
      }]
    }, {
      name: "Treatment Plan Templates",
      route: "/settings/treatment-plan-templates",
      description: "Manage clinic-wide treatment plan templates sent to patients",
      keywords: ["treatment plan", "treatment plans", "templates", "patient plan", "plan of care"],
      permissions: [PERMISSIONS.SETTINGS_TREATMENT_PLAN_TEMPLATES_READ, PERMISSIONS.SETTINGS_TREATMENT_PLAN_TEMPLATES_WRITE],
      children: [{
        name: "New Template",
        route: "/settings/treatment-plan-templates/new",
        keywords: ["create treatment plan template", "new treatment plan template"],
        description: "Create a treatment plan template",
        hidden: true,
        permissions: [PERMISSIONS.SETTINGS_TREATMENT_PLAN_TEMPLATES_WRITE]
      }, {
        name: "Edit Template",
        route: "/settings/treatment-plan-templates/:templateId",
        hidden: true,
        settingsResourceContext: {
          targetId: "tenant.treatmentPlanTemplates",
          recordIdParam: "templateId",
          mode: "edit"
        },
        permissions: [PERMISSIONS.SETTINGS_TREATMENT_PLAN_TEMPLATES_WRITE]
      }]
    }, {
      name: "Forms List",
      moduleName: "FORMS",
      route: "/settings/forms/list",
      keywords: ["forms", "form", "forms list", "form list", "list", "intake"],
      description: "View your available forms",
      permissions: [PERMISSIONS.FORMS_TEMPLATES_READ, PERMISSIONS.FORMS_TEMPLATES_WRITE],
      children: [{
        name: "Create",
        route: "/settings/forms/edit/new",
        keywords: ["create form", "new form", "intake", "build"],
        description: "Create a new form",
        hidden: true,
        permissions: [PERMISSIONS.FORMS_TEMPLATES_WRITE]
      }, {
        name: "Edit",
        route: "/settings/forms/edit/:formId",
        settingsResourceContext: {
          targetId: "tenant.forms",
          recordIdParam: "formId",
          mode: "edit"
        },
        hidden: true,
        permissions: [PERMISSIONS.FORMS_TEMPLATES_WRITE]
      }]
    }, {
      name: "Locations",
      route: "/settings/locations",
      description: "Manage practice locations",
      keywords: ["locations", "offices", "facilities"],
      permissions: [PERMISSIONS.SETTINGS_LOCATIONS_READ],
      // Children, not siblings: the Settings sitemap copies this entry with
      // its children, and only routes in it get breadcrumbs and the top bar.
      children: [{
        name: "Add Location",
        route: "/settings/locations/new",
        description: "Add a practice location",
        keywords: ["add location", "new location", "new office"],
        hidden: true,
        permissions: [PERMISSIONS.SETTINGS_LOCATIONS_WRITE]
      }, {
        name: "Location",
        route: "/settings/locations/:locationId",
        dynamicNameKey: DynamicNameKey.LOCATION,
        description: "A location's details, branding, staff, services, rooms, shift breaks and other settings",
        keywords: ["location branding", "location staff", "location services", "rooms", "shift breaks"],
        hidden: true,
        permissions: [PERMISSIONS.SETTINGS_LOCATIONS_READ]
      }]
    }, {
      name: "Medical Record Numbers",
      route: "/settings/medical-record-numbers",
      description: "Generate MRNs, set location codes and choose which patient ID is shown",
      keywords: ["mrn", "medical record number", "patient id", "chart number", "location code"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Super Bill",
      route: "/settings/super-bill",
      description: "Choose the CPT codes listed first on a Super Bill.",
      keywords: ["super bill", "superbill", "cpt", "hcpcs", "billing codes", "favorites", "frequently used"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Insurance Carriers",
      route: "/settings/insurance-carriers",
      description: "Save insurance carriers and their plans.",
      keywords: ["insurance", "carriers", "carrier", "payers", "payer", "plans", "plan", "claims address", "no-fault", "workers comp"],
      permissions: [PERMISSIONS.SETTINGS_INSURANCE_READ]
    }, {
      name: "Payment Management",
      route: "/settings/payment-management",
      description: "Manage merchant accounts, POS devices, and payment mediums",
      keywords: ["payment", "billing", "POS", "devices", "transactions", "mediums", "settlement", "tax", "taxes", "tax rate", "sales tax", "VAT", "tax management", "tax configuration"],
      permissions: [PERMISSIONS.SETTINGS_PAYMENT_READ]
    }, {
      name: "QuickBooks",
      route: "/settings/quickbooks",
      description: "Connect each location's QuickBooks company and map accounts",
      moduleNames: ["QUICKBOOKS"],
      keywords: ["quickbooks", "accounting", "integration", "oauth", "books", "ledger"],
      permissions: [PERMISSIONS.QUICKBOOKS_MANAGE]
    }, {
      name: "Emails",
      route: "/settings/emails",
      description: "Configure email header, signature, and domain settings",
      keywords: ["email", "header", "signature", "domain", "mailgun", "smtp", "dns", "sending", "footer", "sign-off", "template", "branding"],
      permissions: [PERMISSIONS.SETTINGS_EMAIL_DOMAIN_READ],
      moduleName: "EMAILS"
    }, {
      name: "Spakinect",
      route: "/settings/spakinect",
      description: "Manage Spakinect favorite treatments",
      keywords: ["spakinect", "treatments", "gfe", "good faith exam", "favorites"],
      moduleName: "SPAKINECT",
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Access Labs",
      route: "/settings/access-labs",
      description: "Add the Access client numbers this clinic orders under and assign them to locations",
      keywords: ["access", "access labs", "access medical lab", "comtron", "labgen", "client number", "client id", "lab"],
      moduleName: "ACCESS_LABS",
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "InBody",
      route: "/settings/inbody",
      description: "Connect InBody body composition scans",
      keywords: ["inbody", "lookinbody", "body composition", "scan", "api key"],
      moduleName: "INBODY",
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Studio Mode",
      route: "/settings/photo-studio",
      description: "Default photo backdrop, edge sharpening and marketing export branding",
      keywords: ["studio", "photo", "photos", "backdrop", "background", "before and after", "gallery", "watermark", "disclaimer"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Coupa",
      route: "/settings/coupa",
      description: "Sync inventory with Coupa",
      keywords: ["coupa", "procurement", "inventory", "warehouse", "purchasing", "blind count"],
      moduleName: "COUPA",
      // View Inventory opens only its Decoda vs Coupa tab.
      permissions: [PERMISSIONS.SETTINGS_COUPA_READ, PERMISSIONS.INVENTORY_READ]
    }, {
      name: "GFE Treatments",
      route: "/settings/gfe-treatments",
      description: "Treatments your providers clear in an in-house GFE",
      keywords: ["gfe", "good faith exam", "in-house gfe", "clearance", "treatments"],
      moduleName: "IN_HOUSE_GFE",
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Qualiphy",
      route: "/settings/qualiphy",
      description: "Configure Qualiphy GFE integration",
      keywords: ["qualiphy", "gfe", "good faith exam", "telehealth", "api key"],
      moduleName: "QUALIPHY",
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Rules",
      route: "/settings/rules",
      description: "Manage payment settings and devices",
      keywords: ["payment", "billing", "POS", "devices", "transactions"],
      permissions: [PERMISSIONS.SETTINGS_RULES_READ]
    }, {
      name: "Tasks",
      route: "/settings/tasks",
      description: "Configure task automation and groups",
      keywords: ["tasks", "automation", "alerts", "groups", "assignment"],
      permissions: [PERMISSIONS.TASKS_WRITE]
    }, {
      name: "Teams",
      route: "/settings/teams",
      description: "Organize providers into teams",
      keywords: ["teams", "groups", "providers", "organization"],
      permissions: [PERMISSIONS.TASKS_WRITE]
    }, {
      name: "Care Plan Templates",
      route: "/settings/care-plans",
      description: "Create and manage care plan templates",
      keywords: ["care plan templates", "care plan template", "care plans", "care plan", "templates", "treatment", "protocol"],
      moduleName: "CARE_PLANS",
      permissions: [PERMISSIONS.SETTINGS_READ, PERMISSIONS.CARE_PLAN_TEMPLATES_WRITE],
      children: [{
        name: "New Care Plan Template",
        route: "/settings/care-plans/:templateId",
        settingsResourceContext: {
          targetId: "tenant.care_plan_templates",
          recordIdParam: "templateId",
          mode: "edit"
        },
        description: "Create and manage care plan templates",
        keywords: ["care plan templates", "care plan template", "care plans", "care plan", "templates", "treatment", "protocol"],
        moduleName: "CARE_PLANS",
        permissions: [PERMISSIONS.SETTINGS_READ, PERMISSIONS.CARE_PLAN_TEMPLATES_WRITE],
        dynamicNameKey: DynamicNameKey.CARE_PLAN_TEMPLATE,
        hidden: true
      }]
    }, {
      name: "Measurements",
      route: "/settings/measurements",
      description: "Configure measurement fields and groups for tracking patient metrics",
      keywords: ["measurements", "vitals", "metrics", "tracking", "patient measurements", "groups", "fields"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Social",
      route: "/settings/social",
      description: "Manage social messaging accounts and patient linking",
      moduleName: "SOCIALS",
      keywords: ["social", "instagram", "facebook", "messenger", "accounts", "patient linking", "DM"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }]
  }, {
    name: "Patient Experience",
    description: "Manage the patient-facing brand, scheduling, and shop experience",
    children: [{
      name: "Patient Portal",
      route: "/settings/patient-portal",
      description: "Configure the patient portal dashboard layout",
      keywords: ["patient portal", "dashboard", "widgets", "layout", "customization"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Brand",
      route: "/settings/brand",
      description: "Customize the practice details, logo, and colors patients see",
      keywords: ["brand", "branding", "logo", "practice name", "introductory text", "colors", "theme", "receipt", "invoice", "self-schedule", "booking", "appearance"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Self Scheduling",
      route: "/settings/self-schedule/general",
      description: "Manage scheduling settings",
      keywords: ["blacklisted patients", "provider capacities", "scheduling", "appointments", "bookings", "self-schedule", "self scheduling"],
      permissions: [PERMISSIONS.SETTINGS_SCHEDULING_READ]
    }, {
      name: "Booking Events",
      route: "/settings/self-schedule/events",
      description: "Publish a booking link confined to one day and set of hours",
      keywords: ["booking event", "event link", "open house", "pop-up", "event night", "booking window", "specific hours", "self-schedule", "self scheduling"],
      permissions: [PERMISSIONS.SETTINGS_SCHEDULING_READ]
    }, {
      name: "Self-Scheduling Appearance",
      route: "/settings/self-schedule/service-order",
      description: "Customize the banner and reorder services",
      keywords: ["self-scheduling appearance", "banner image", "service order", "reorder", "drag", "drop", "order", "category", "categories", "services", "self-schedule", "self scheduling"],
      permissions: [PERMISSIONS.SETTINGS_SCHEDULING_READ]
    }, {
      name: "Shop Catalog",
      route: "/settings/brand/shop",
      moduleName: "SHOP",
      description: "Preview, show, hide, and arrange the online shop catalog",
      keywords: ["shop", "online shop", "catalog", "categories", "promotions", "gift cards", "packages", "services", "products", "medications", "storefront", "order", "visibility"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }]
  }, {
    name: "Items",
    description: "Manage items and related configurations",
    children: [{
      name: "Products",
      route: "/settings/products",
      description: "Manage inventory products",
      keywords: ["products", "inventory", "items"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Gift Cards",
      route: "/settings/gift-cards",
      moduleName: "GIFT_CARDS",
      description: "Manage gift card denominations and view issued cards",
      keywords: ["gift cards", "gift", "cards", "vouchers"],
      permissions: [PERMISSIONS.INVENTORY_READ]
    }, {
      name: "Loyalty Points",
      route: "/settings/loyalty-points",
      moduleName: "LOYALTY_POINTS",
      description: "Configure loyalty point value and expiry settings",
      keywords: ["loyalty", "points", "rewards", "redemption", "earn"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Approvals",
      route: "/settings/approvals",
      description: "Request changes, review approvals and configure policies",
      permissions: [PERMISSIONS.SETTINGS_SERVICES_WRITE, PERMISSIONS.SETTINGS_PRODUCTS_WRITE, PERMISSIONS.SETTINGS_MEDICATIONS_WRITE, PERMISSIONS.MEMBERSHIPS_WRITE, PERMISSIONS.SETTINGS_PACKAGES_WRITE, PERMISSIONS.SETTINGS_ROLES_WRITE, PERMISSIONS.APPROVALS_REVIEW, PERMISSIONS.APPROVALS_POLICIES_WRITE, PERMISSIONS.PATIENTS_EXPORT]
    }, {
      name: "Services",
      route: "/settings/services",
      description: "Configure services and settings",
      keywords: ["services", "appointments", "scheduling", "booking"],
      permissions: [PERMISSIONS.SETTINGS_SERVICES_READ]
    }, {
      name: "Bookables",
      route: "/settings/bookables",
      description: "Manage rooms, equipment, and resources for scheduling",
      keywords: ["bookables", "appointments", "scheduling", "booking", "rooms", "equipment", "resources"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Medications",
      route: "/settings/medications",
      description: "Manage medication products and templates",
      keywords: ["medications", "items", "billing", "cart", "templates"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Packages",
      route: "/settings/packages",
      description: "Manage product packages and bundles",
      keywords: ["packages", "bundles", "inventory", "products"],
      permissions: [PERMISSIONS.SETTINGS_READ],
      children: [{
        name: "Edit Package",
        route: "/settings/packages/edit/:packageId",
        settingsResourceContext: {
          targetId: "tenant.packages",
          recordIdParam: "packageId",
          mode: "edit"
        }
      }]
    }, {
      name: "Package Usage",
      route: "/settings/packages/package-usage/:packageId",
      settingsResourceContext: {
        targetId: "tenant.packages",
        recordIdParam: "packageId",
        mode: "usage"
      },
      description: "View package usage details",
      keywords: ["packages", "usage"],
      hidden: true,
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Memberships",
      route: "/settings/memberships",
      description: "Manage memberships",
      keywords: ["memberships", "subscriptions", "inventory"],
      permissions: [PERMISSIONS.MEMBERSHIPS_READ]
    }, {
      name: "Promotions",
      route: "/settings/promotions",
      moduleName: "PAYMENTS",
      keywords: ["promotions", "promo", "deals", "offers", "discounts", "coupon", "bogo", "referral"],
      description: "Create and manage promotions",
      permissions: [PERMISSIONS.PROMOTIONS_READ]
    }, {
      name: "Referral Programs",
      route: "/settings/referral-programs",
      keywords: ["referral", "referrals", "refer a friend", "invite", "reward"],
      description: "Reward patients for referring friends",
      moduleName: "REFERRALS",
      permissions: [PERMISSIONS.REFERRAL_PROGRAMS_READ]
    }, {
      name: "Create Promotion",
      route: "/settings/promotions/new",
      moduleName: "PAYMENTS",
      keywords: ["create", "new", "promotion", "deal", "offer"],
      description: "Create a new promotion",
      hidden: true
    }, {
      name: "Promotion Detail",
      route: "/settings/promotions/:promotionId",
      settingsResourceContext: {
        targetId: "tenant.promotions",
        recordIdParam: "promotionId",
        mode: "edit"
      },
      moduleName: "PAYMENTS",
      keywords: ["edit", "promotion", "detail", "update"],
      description: "View or edit a promotion",
      hidden: true
    }, {
      name: "Edit Membership",
      route: "/settings/memberships/edit/:membershipId",
      settingsResourceContext: {
        targetId: "tenant.memberships",
        recordIdParam: "membershipId",
        mode: "edit"
      },
      description: "Edit membership details",
      keywords: ["memberships", "edit"],
      hidden: true,
      permissions: [PERMISSIONS.MEMBERSHIPS_READ]
    }, {
      name: "Add Member",
      route: "/settings/memberships/add-member",
      description: "Add a new member to a membership",
      keywords: ["memberships", "members", "add"],
      hidden: true,
      permissions: [PERMISSIONS.MEMBERSHIPS_READ]
    }, {
      name: "Categories",
      route: "/settings/categories",
      description: "Manage categories for services, items, and stock",
      keywords: ["categories", "organization", "classification"],
      permissions: [PERMISSIONS.SETTINGS_READ]
    }, {
      name: "Commissions",
      route: "/settings/commissions",
      moduleName: "COMMISSIONS",
      description: "Manage commission structures, tiers, and provider assignments",
      keywords: ["commissions", "commission structures", "tiers", "payouts", "provider pay"],
      permissions: [PERMISSIONS.BILLING_COMMISSIONS_WRITE],
      children: [{
        name: "Commission Detail",
        route: "/settings/commissions/:commissionId",
        settingsResourceContext: {
          targetId: "tenant.commissions",
          recordIdParam: "commissionId",
          mode: "view"
        }
      }]
    }]
  }, {
    name: "AI Assistants",
    description: "Configure AI assistants and automation",
    children: [{
      name: "Phone Numbers",
      route: "/settings/phone-numbers",
      description: "Manage provisioned phone numbers and forwarding",
      keywords: ["phone", "numbers", "telephony", "provision", "forwarding"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_INBOUND_READ],
      children: [{
        name: "Configuration",
        route: "/settings/phone-numbers/:id",
        settingsResourceContext: {
          targetId: "tenant.phoneNumber",
          recordIdParam: "id",
          mode: "edit"
        }
      }]
    }, {
      name: "Desk Phones",
      route: "/settings/desk-phones",
      description: "Provision physical SIP desk phones for ring groups",
      keywords: ["desk", "phone", "sip", "physical", "yealink", "polycom", "grandstream", "telephony", "hardware"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_INBOUND_READ]
    }, {
      name: "Extensions",
      route: "/settings/extensions",
      description: "Short dial codes for transferring calls between phones",
      keywords: ["extension", "dial", "code", "transfer", "star 8", "*8", "short code", "telephony", "desk phone", "ring group"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_INBOUND_READ]
    }, {
      name: "Tagging",
      route: "/settings/call-tags",
      description: "Labels Decoda applies to calls from the transcript, and when to use each one",
      keywords: ["call tags", "call tagging", "tags", "tagging", "labels", "categorize", "classifier", "transcript", "call inbox", "filter calls"],
      permissions: [PERMISSIONS.SETTINGS_TAGS_READ]
    }, {
      name: "Outbound Calling",
      route: "/settings/assistants/outbound",
      description: "Configure AI assistant settings",
      keywords: ["assistant", "AI", "automation", "virtual assistant"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_OUTBOUND_READ]
    }, {
      name: "Call Summarizer",
      route: "/settings/assistants/call-summarizer",
      description: "Configure AI assistant settings",
      keywords: ["assistant", "AI", "automation", "virtual assistant"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_SUMMARIZER_READ]
    }, {
      name: "Texting Assistant",
      route: "/settings/assistants/inbound-texting",
      description: "Configure AI assistant settings",
      keywords: ["assistant", "AI", "automation", "virtual assistant"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_TEXTING_READ]
    }, {
      name: "Scribe Assistant",
      route: "/settings/assistants/scribe",
      description: "Configure AI assistant settings",
      keywords: ["assistant", "AI", "automation", "virtual assistant"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_SCRIBE_READ]
    }, {
      name: "Web Chat Widget",
      route: "/settings/assistants/web-chat",
      description: "Configure web chat widget for your website",
      keywords: ["web chat", "widget", "embed", "website", "assistant", "AI"],
      permissions: [PERMISSIONS.SETTINGS_ASSISTANTS_TEXTING_READ]
    }]
  }, {
    name: "Developers",
    description: "API access and webhook management",
    permissions: [PERMISSIONS.SETTINGS_DEVELOPERS_READ],
    children: [{
      name: "API & Webhooks",
      route: "/settings/developers",
      description: "Manage API keys and webhook configurations",
      keywords: ["api", "webhooks", "integration", "developer", "keys"],
      permissions: [PERMISSIONS.SETTINGS_DEVELOPERS_READ]
    }, {
      name: "Embedded Payment Components",
      route: "/settings/developers/embed-payments",
      description: "Generate embeddable payment and store details forms for your website",
      keywords: ["embed", "payment", "store details", "iframe", "component"],
      permissions: [PERMISSIONS.SETTINGS_DEVELOPERS_READ]
    }]
  }]
}];
function legacySetting(route: string): SiteMapItem | undefined {
  const visit = (items: SiteMapItem[]): SiteMapItem | undefined => {
    for (const item of items) {
      if (item.route === route) return item;
      const match = item.children ? visit(item.children) : undefined;
      if (match) return match;
    }
  };
  return visit(legacySettingsConfig);
}
function findLegacySetting(route: string): SiteMapItem {
  const item = legacySetting(route);
  if (!item) throw new Error(`Unknown settings destination: ${route}`);
  return item;
}
function setting(route: string, overrides: Partial<SiteMapItem> = {}, /** Terms from other domains this destination also answers to in search. */
extraKeywords: string[] = []): SiteMapItem {
  const item = findLegacySetting(route);
  return {
    ...item,
    ...overrides,
    keywords: Array.from(new Set([...(overrides.keywords ?? item.keywords ?? []), ...extraKeywords])),
    // Detail/create routes still participate in breadcrumbs and the command bar,
    // but the directory itself remains exactly two levels deep.
    children: item.children?.map(child => ({
      ...child,
      hidden: true
    })),
    destinationKind: "canonical"
  };
}
const settingsSection = (name: string, route: string, description: string, keywords: string[], permissions: string[] = [PERMISSIONS.SETTINGS_READ]): SiteMapItem => ({
  name,
  route,
  description,
  keywords,
  permissions,
  canonicalRoute: route,
  destinationKind: "canonical"
});

/** The settings directory is intentionally limited to group + destination. */
export const settingsConfig: SiteMapItem[] = [{
  name: "Settings",
  route: "/settings",
  description: "Browse and search all settings",
  keywords: ["settings", "configuration", "preferences", "administration"],
  children: [{
    name: "Setup Guide",
    route: "/settings/setup-guide",
    description: "Work through everything your clinic needs to set up",
    keywords: ["setup", "onboarding", "checklist", "go-live", "getting started"],
    moduleName: "SETUP_CHECKLIST",
    permissions: [PERMISSIONS.SETTINGS_READ]
  }, {
    name: "My Decoda",
    description: "Your profile, preferences, and personal connections",
    children: [setting("/settings/profile"), setting("/settings/preferences"), setting("/settings/notifications"), setting("/settings/google-calendar"), setting("/settings/beta-features"), setting("/settings/fullscript"), setting("/settings/coupa-sign-in")]
  }, {
    name: "Scheduling",
    description: "Clinic scheduling, availability, and calendar setup",
    children: [{
      name: "Shifts",
      route: "/settings/shifts",
      canonicalRoute: "/settings/shifts",
      destinationKind: "canonical",
      description: "Manage shifts, blocks, holidays, availability, and conflicts",
      moduleName: "SCHEDULING",
      permissions: [PERMISSIONS.SCHEDULING_SHIFTS_WRITE],
      keywords: ["shifts", "blocks", "holidays", "availability", "conflicts", "staff"]
    }, setting("/settings/bookables"), setting("/settings/self-schedule/general", {
      name: "Self Scheduling"
    }, ["patient experience"]), setting("/settings/self-schedule/events", {
      name: "Booking Events"
    }, ["patient experience"]), setting("/settings/self-schedule/service-order"), setting("/settings/calendar-appearance"), setting("/settings/locations"), settingsSection("Scheduling Defaults", "/settings/scheduling-defaults", "Configure clinic-wide scheduling defaults", ["scheduling", "defaults", "calendar"]), settingsSection("Clinic Google Calendar", "/settings/clinic-google-calendar", "Manage the clinic's centralized Google Calendar connection", ["google calendar", "clinic calendar", "sync"])]
  }, {
    name: "Patient Experience",
    description: "Patient-facing forms, communications, branding, and booking",
    children: [setting("/settings/forms/list", {
      name: "Forms"
    }, ["clinical"]), setting("/settings/patient-portal"), setting("/settings/brand", {
      name: "Branding"
    }, ["receipts"]), setting("/settings/brand/shop", {
      name: "Shop"
    }, ["growth"]), settingsSection("Patient Settings", "/settings/patient-settings", "Configure patient information requirements", ["patients", "required fields", "requirements"]), settingsSection("Patient Tags", "/settings/patient-tags", "Manage patient tags", ["patients", "tags"]), setting("/settings/medical-record-numbers", {}, ["patients"]), settingsSection("Communications", "/settings/communications", "Configure patient communication defaults", ["communications", "email", "sms", "patients"]), setting("/settings/emails", {}, ["growth"])]
  }, {
    name: "Clinical",
    description: "Clinical documentation, prescribing, and care workflows",
    children: [setting("/settings/note-templates"), setting("/settings/treatment-plan-templates"), setting("/settings/spakinect", {
      name: "Spakinect"
    }), setting("/settings/qualiphy"), setting("/settings/gfe-treatments"), setting("/settings/inbody", {
      name: "InBody"
    }), setting("/settings/photo-studio", {
      name: "Studio Mode"
    }, ["patient photos"]), setting("/settings/access-labs"), setting("/settings/care-plans"), setting("/settings/measurements"), setting("/settings/lab-panels"), setting("/settings/suggestions"), settingsSection("Document Review", "/settings/document-review", "Configure document review and sign-off", ["documents", "review", "sign-off"]), settingsSection("Clinical Notes", "/settings/clinical-notes", "Require a prescriber co-signature on notes", ["notes", "sign-off", "co-sign", "prescriber"])]
  }, {
    name: "E-Prescribing",
    description: "DoseSpot prescriber enrollment and prescription presets",
    children: [setting("/settings/e-prescribing", {
      name: "Enrollment"
    }), setting("/settings/prescription-presets", {
      name: "Presets"
    })]
  }, {
    name: "Billing",
    description: "Payments, insurance, invoicing, and accounting defaults",
    children: [setting("/settings/payment-management"), setting("/settings/insurance-carriers"), setting("/settings/super-bill"), setting("/settings/rules", {
      name: "Billing Rules"
    }), setting("/settings/developers/embed-payments"), settingsSection("Payment Settings", "/settings/payment-settings", "Configure payment and checkout behaviour, and how checkout tabs and items are sorted", ["payments", "checkout", "fees", "tips", "sort", "item sort order", "display order", "checkout tabs", "categories"]), settingsSection("Invoicing", "/settings/invoicing", "Configure invoicing defaults", ["invoices", "invoicing", "defaults"]), settingsSection("Accounting Basis", "/settings/analytics-defaults", "Choose when revenue is counted by default", ["accounting basis", "cash", "accrual", "revenue"]), setting("/settings/quickbooks")]
  }, {
    name: "Growth",
    description: "Growth tools, integrations, offers, and loyalty",
    children: [{
      name: "Pipelines",
      route: "/settings/pipelines",
      canonicalRoute: "/settings/pipelines",
      destinationKind: "canonical",
      description: "Configure lead pipelines and stages",
      moduleName: "MARKETING",
      keywords: ["pipelines", "growth", "leads", "opportunities"]
    }, {
      name: "Lead Tags",
      route: "/settings/opportunity-tags",
      canonicalRoute: "/settings/opportunity-tags",
      destinationKind: "canonical",
      description: "Manage tags for leads on the pipeline board",
      moduleName: "MARKETING",
      keywords: ["leads", "opportunities", "tags", "labels", "growth", "crm"]
    }, {
      name: "Integrations",
      route: "/settings/integrations",
      canonicalRoute: "/settings/integrations",
      destinationKind: "canonical",
      description: "Connect growth and advertising platforms",
      moduleName: "MARKETING",
      keywords: ["integrations", "growth", "ads"]
    }, {
      name: "AI Agents",
      route: "/settings/ai-agents",
      canonicalRoute: "/settings/ai-agents",
      destinationKind: "canonical",
      description: "Configure and run AI-powered growth agents",
      moduleName: "MARKETING",
      keywords: ["agents", "ai", "growth"]
    }, setting("/settings/memberships"), setting("/settings/promotions"), setting("/settings/referral-programs"), setting("/settings/loyalty-points"), setting("/settings/social")]
  }, {
    name: "Staff",
    description: "Users, roles, teams, shifts, and workspace access",
    children: [setting("/settings/users"), setting("/settings/roles", {
      name: "Roles & Permissions"
    }), setting("/settings/teams"), setting("/settings/commissions"), setting("/settings/tasks"), settingsSection("Workspace Navigation", "/settings/workspace-navigation", "Choose the clinic's default home page", ["workspace", "navigation", "home page", "default page"]), settingsSection("Authentication", "/settings/auth", "Configure authentication, sessions, and network restrictions", ["authentication", "sessions", "ip", "clock-in", "microsoft", "microsoft 365", "entra", "sso"],
    // Two surfaces share this page: the access controls post to
    // tenant/security-settings, Microsoft sign-in is settings:write.
    [PERMISSIONS.SETTINGS_SECURITY_WRITE, PERMISSIONS.SETTINGS_WRITE])]
  }, {
    name: "Catalog",
    description: "Products, services, medications, packages, and stock",
    children: [setting("/settings/approvals"), setting("/settings/products"), setting("/settings/services"), setting("/settings/medications", {}, ["clinical"]), setting("/settings/packages"), setting("/settings/gift-cards"), {
      name: "Stock",
      route: "/settings/stock",
      canonicalRoute: "/settings/stock",
      destinationKind: "canonical",
      description: "Manage stock levels and suppliers",
      moduleName: "INVENTORY",
      permissions: [PERMISSIONS.INVENTORY_READ],
      keywords: ["stock", "inventory", "suppliers"]
    }, setting("/settings/coupa", {
      name: "Coupa"
    }), setting("/settings/categories")]
  }, {
    name: "Phones",
    description: "Phone numbers, desk phones, and dial extensions",
    children: [setting("/settings/phone-numbers", {
      name: "Numbers"
    }, ["phones"]), setting("/settings/desk-phones", {
      name: "Devices"
    }, ["staff"]), setting("/settings/extensions"), setting("/settings/call-tags", {
      name: "Tagging"
    })]
  }, {
    name: "Assistants",
    description: "Calling, texting, scribe, chat, and task assistants",
    children: [setting("/settings/assistants/outbound"), setting("/settings/assistants/call-summarizer"), setting("/settings/assistants/inbound-texting"), setting("/settings/assistants/scribe", {}, ["clinical"]), setting("/settings/assistants/web-chat", {}, ["patient experience"])]
  }, {
    name: "Developers",
    description: "API access and webhook management",
    children: [setting("/settings/developers")]
  }]
}];

/**
 * Analytics is its own sub-navigation, like Settings: one root plus a group per
 * theme. Built from `analyticsDashboardMeta` so the sidebar, the command bar,
 * and the dashboard registry can never drift apart.
 */
export const analyticsConfig: SiteMapItem[] = [{
  name: "Analytics",
  route: ANALYTICS_ROOT,
  description: "Dashboards and reports across the clinic",
  keywords: ["analytics", "dashboards", "reports", "metrics", "insights"],
  permissions: ANALYTICS_SECTION_PERMISSIONS,
  children: [...groupAnalyticsDashboards(analyticsDashboardMeta).map(group => ({
    name: group.name,
    children: group.entries.map(entry => ({
      name: entry.name,
      route: entry.route,
      description: entry.description,
      keywords: entry.keywords,
      permissions: entry.permissions,
      allPermissions: entry.allPermissions,
      moduleNames: entry.requiredModules,
      moduleName: entry.sectionModule,
      tenantSubDomains: entry.tenantSubDomains,
      routeAliases: entry.routeAliases
    }))
  })), {
    name: "Scheduled Reports",
    route: "/analytics/scheduled-reports",
    description: "Email a dashboard or report on a recurring schedule",
    keywords: ["scheduled", "reports", "email", "recurring", "subscription"],
    permissions: SCHEDULED_REPORTS_PERMISSIONS
  }, {
    // Custom dashboards are rows the tenant creates, so they cannot be
    // listed here. This pattern carries their access policy so the layout
    // gates them the same way it gates every built-in destination.
    name: "Dashboard",
    route: "/analytics/:dashboardId",
    permissions: [PERMISSIONS.ANALYTICS_READ],
    hidden: true
  }, {
    name: "New Dashboard",
    route: "/analytics/new-dashboard",
    description: "Start a custom dashboard of your own widgets",
    keywords: ["new", "create", "custom", "dashboard", "analytics"],
    permissions: [PERMISSIONS.ANALYTICS_WRITE]
  }, {
    name: "Create Report",
    route: "/analytics/create",
    permissions: [PERMISSIONS.ANALYTICS_WRITE],
    hidden: true
  }]
}];

/** Flat sidebar shape: the section's own row, then each group section. */
export const analyticsSidebarConfig: SiteMapItem[] = [{
  ...analyticsConfig[0],
  children: undefined
}, ...(analyticsConfig[0].children ?? [])];
export const settingsSidebarConfig: SiteMapItem[] = [{
  ...settingsConfig[0],
  children: undefined
}, ...(settingsConfig[0].children ?? [])];

/**
 * A top-level section that replaces the main nav with its own sub-nav. The
 * active one is chosen by pathname, so adding a section is a row here rather
 * than another mode flag threaded through the navigation components.
 */
export interface NavSection {
  id: string;
  /** Route prefix that hands the sidebar over to this section. */
  prefix: string;
  /** The section's flat sidebar: its title row, then its groups. */
  sidebar: SiteMapItem[];
  /** Rows within one route are distinguished by the `section` param. */
  sectionParamRows?: boolean;
  /** Nav hrefs carry the filters in force, so they survive a move between rows. */
  carriesFilters?: boolean;
  /** The section lists tenant-created destinations alongside its static ones. */
  customDashboards?: boolean;
}
export const NAV_SECTIONS: NavSection[] = [{
  id: "settings",
  prefix: "/settings",
  sidebar: settingsSidebarConfig
}, {
  id: "analytics",
  prefix: ANALYTICS_ROOT,
  sidebar: analyticsSidebarConfig,
  sectionParamRows: true,
  carriesFilters: true,
  customDashboards: true
}];

/** The section a route belongs to, or undefined for the main nav. */
export function findNavSection(route: string | undefined): NavSection | undefined {
  if (!route) return undefined;
  return NAV_SECTIONS.find(section => route === section.prefix || route.startsWith(`${section.prefix}/`));
}

/** Clinic dashboards HQ runs across its clinics, each at `/enterprise/analytics/<id>`. */
const ENTERPRISE_CLINIC_REPORTS = ["product-sales-breakdown", "payment-breakdown", "provider-earnings", "payment-plan-analytics", "credit-gift-card-activity", "memberships", "accounting-overview"];

// Named, described and gated like the clinic dashboard; HQ needs none of the clinic's modules.
function enterpriseClinicReport(id: string): SiteMapItem[] {
  const meta = getAnalyticsDashboardMeta(id);
  if (!meta) return [];
  return [{
    name: meta.name,
    route: `/enterprise/analytics/${id}`,
    description: meta.description,
    keywords: meta.keywords,
    permissions: meta.permissions,
    allPermissions: meta.allPermissions
  }];
}

// A MASTER tenant's whole sidebar; see isEnterpriseRoute.
export const enterpriseSiteMap: SiteMapItem[] = [{
  name: "Approvals",
  route: "/enterprise/approvals",
  description: "Review clinic requests and manage service approval policies",
  permissions: [PERMISSIONS.APPROVALS_REVIEW, PERMISSIONS.APPROVALS_POLICIES_WRITE]
}, {
  name: "Notifications",
  description: "View and manage your notifications",
  keywords: ["notifications", "alerts", "inbox"],
  action: "notifications"
}, {
  name: "Analytics",
  route: "/enterprise/analytics",
  description: "Revenue, patients, retention and product lines across every clinic in the group",
  keywords: ["enterprise", "roll-up", "rollup", "group", "multi-location", "clinics", "brands", "consolidated", "analytics"],
  icon: <AiOutlineLineChart />,
  permissions: [PERMISSIONS.ANALYTICS_READ],
  // Each report is its own page; the first is also the section's landing page.
  children: [{
    name: "Performance",
    route: "/enterprise/analytics",
    description: "Revenue, visits and patients by clinic, location and provider",
    keywords: ["performance", "revenue", "visits", "roll-up"],
    permissions: [PERMISSIONS.ANALYTICS_READ]
  }, {
    name: "Product lines",
    route: "/enterprise/analytics/product-lines",
    description: "Every clinic's sales under HQ's shared catalog and categories",
    keywords: ["product lines", "catalog", "categories", "sales"],
    permissions: [PERMISSIONS.ANALYTICS_READ]
  }, {
    name: "Patients",
    route: "/enterprise/analytics/patients",
    description: "New patients and how many come back, by clinic",
    keywords: ["patients", "retention", "cohort", "rebook"],
    permissions: [PERMISSIONS.ANALYTICS_READ]
  }, {
    name: "Discounts & promotions",
    route: "/enterprise/analytics/discounts",
    description: "What discounts and promotions cost across clinics",
    keywords: ["discounts", "promotions", "promos"],
    permissions: [PERMISSIONS.ANALYTICS_READ]
  }, {
    name: "Refunds",
    route: "/enterprise/analytics/refunds",
    description: "How much each clinic refunded, against what it collected",
    keywords: ["refunds", "refund rate", "returns"],
    permissions: [PERMISSIONS.ANALYTICS_READ]
  }, ...ENTERPRISE_CLINIC_REPORTS.flatMap(enterpriseClinicReport)]
}, {
  name: "Inventory",
  route: "/enterprise/inventory",
  moduleName: "COUPA",
  description: "Stock on hand at every clinic in the group, under HQ's stock records",
  keywords: ["inventory", "stock", "stock on hand", "on hand", "roll-up", "rollup", "reorder", "low stock", "clinics"],
  icon: <AiOutlineInbox />,
  permissions: [PERMISSIONS.INVENTORY_READ]
}, {
  name: "Shared catalog",
  description: "What every clinic in the group sells, authored once",
  children: [setting("/settings/services"), setting("/settings/products"), setting("/settings/medications"), setting("/settings/packages"), setting("/settings/memberships"), setting("/settings/promotions"), setting("/settings/categories"),
  // Inventory pages, which `setting()` cannot find among the settings.
  {
    name: "Stock",
    route: "/settings/stock",
    moduleName: "COUPA",
    description: "The stock items every clinic counts and orders",
    keywords: ["stock", "inventory", "item master"],
    permissions: [PERMISSIONS.INVENTORY_READ]
  }, {
    name: "Suppliers",
    route: "/inventory/suppliers",
    moduleName: "COUPA",
    description: "The suppliers every clinic orders from",
    keywords: ["suppliers", "vendor", "vendors"],
    permissions: [PERMISSIONS.INVENTORY_READ]
  }]
}, {
  name: "Clinical templates",
  description: "Forms, notes and measurements every clinic works from",
  children: [setting("/settings/forms/list", {
    name: "Forms"
  }), setting("/settings/note-templates"), setting("/settings/measurements")]
}, {
  name: "Team",
  description: "Who can work in the group, and what they can do",
  children: [setting("/settings/users"), setting("/settings/roles"), setting("/settings/notifications", {
    moduleName: "COUPA"
  })]
}, {
  name: "Purchasing",
  description: "The Coupa login, commodity rules, item exceptions, adjustment codes and stock options every clinic uses, and requisitions waiting on you",
  children: [setting("/settings/coupa", {
    name: "Coupa"
  }), {
    name: "Requisition approvals",
    route: "/enterprise/requisition-approvals",
    moduleName: "COUPA",
    description: "Coupa requisitions waiting on you, at every clinic",
    keywords: ["approvals", "approve", "reject", "requisitions", "requisition", "coupa", "purchasing"],
    permissions: [PERMISSIONS.INVENTORY_ORDERS_APPROVE]
  }, {
    name: "Coupa drift",
    route: "/enterprise/coupa-drift",
    moduleName: "COUPA",
    description: "Which clinics' stock no longer matches Coupa",
    keywords: ["coupa", "drift", "reconciliation", "mismatch", "clinics"],
    permissions: [PERMISSIONS.SETTINGS_COUPA_READ]
  }]
}];

// Section prefixes, so detail/edit routes (/settings/forms/edit/:id) match too.
// A section keeps a module only when HQ's entry needs one its clinics' page
// doesn't (Notifications needs COUPA); a master without it has no such page.
const ENTERPRISE_SECTIONS = enterpriseSiteMap.flatMap(item => [item, ...(item.children ?? [])]).flatMap(item => item.route ? [{
  prefix: item.route.replace(/\/list$/, ""),
  moduleName: item.moduleName === legacySetting(item.route)?.moduleName ? undefined : item.moduleName
}] : []);

// Under a shared section, but about stock held at a location, which HQ has none of.
const CLINIC_ONLY_ROUTES = ["/settings/stock/count", "/settings/stock/uploads"];
const under = (route: string, section: string) => route === section || route.startsWith(`${section}/`);
const enterpriseSectionsOf = (route: string | undefined) => route && !CLINIC_ONLY_ROUTES.some(only => under(route, only)) ? ENTERPRISE_SECTIONS.filter(({
  prefix
}) => under(route, prefix)) : [];

/** The module an enterprise page needs, when it needs one. */
export function enterpriseRouteModule(route: string): Module | undefined {
  return enterpriseSectionsOf(route).find(({
    moduleName
  }) => moduleName)?.moduleName;
}
export function isEnterpriseRoute(route: string | undefined, enabledModules: readonly Module[] | undefined): boolean {
  return enterpriseSectionsOf(route).some(({
    moduleName
  }) => !moduleName || !!enabledModules?.includes(moduleName));
}
type TenantKindOf = Pick<Tenant, "kind"> | null | undefined;
export function siteMapFor(tenant: TenantKindOf): SiteMapItem[] {
  return tenant?.kind === "MASTER" ? enterpriseSiteMap : providerSiteMap;
}
export function tenantHasRoute(tenant: (Pick<Tenant, "kind"> & Partial<Pick<Tenant, "enabledModules">>) | null | undefined, route: string): boolean {
  return tenant?.kind === "MASTER" ? isEnterpriseRoute(route, tenant.enabledModules) : !route.startsWith("/enterprise/");
}
export const flattenSiteMap = (items: SiteMapItem[]): SiteMapItem[] => {
  return items.reduce<SiteMapItem[]>((acc, item) => {
    if (item.route) {
      acc.push(item);
    }
    if (item.children) {
      // Recursively flatten all children, including those in sections.
      // Include any child that has a route (even if it also has nested children),
      // same as the top-level branch — otherwise navigable pages like "Forms List"
      // are dropped because they sit above Create/Edit sub-routes.
      const childRoutes = item.children.reduce<SiteMapItem[]>((childAcc, child) => {
        if (child.route) {
          childAcc.push(child);
        }
        if (child.children) {
          childAcc.push(...flattenSiteMap(child.children));
        }
        return childAcc;
      }, []);
      acc.push(...childRoutes);
    }
    return acc;
  }, []);
};

/** Every page, for lookup by route — not what a tenant may search. */
export const getAllCommandBarRoutes = () => [...flattenSiteMap(providerSiteMap), ...flattenSiteMap(analyticsConfig), ...flattenSiteMap(settingsConfig), ...flattenSiteMap(enterpriseSiteMap)];
const commandBarRoutesFor = (tenant: TenantKindOf) => tenant?.kind === "MASTER" ? flattenSiteMap(enterpriseSiteMap) : [...flattenSiteMap(providerSiteMap), ...flattenSiteMap(analyticsConfig), ...flattenSiteMap(settingsConfig)];

// Convert route patterns to a regex. Supports ONLY Express-style params:
// - Param: "/path/:id" -> "/path/([^/]+)"
// - Catch-all: "/path/:slug*" -> "/path/(.+)"
// - Optional trailing slash
const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const routePatternToRegex = (pattern: string): RegExp => {
  const segments = pattern.split("/");
  const regexBody = segments.map(seg => {
    if (seg === "") return "";
    // Express-style param: :id or :id*
    if (/^:.+\*$/.test(seg)) return "(.+)"; // :param*
    if (/^:.+/.test(seg)) return "([^/]+)"; // :param
    return escapeRegex(seg);
  }).join("/");
  return new RegExp(`^${regexBody}/?$`);
};
export const matchesRoutePattern = (pattern: string | undefined, path: string): boolean => {
  if (!pattern) return false;
  try {
    const regex = routePatternToRegex(pattern);
    return regex.test(path);
  } catch {
    return false;
  }
};

// Modified function that filters out billing dashboard for specific users
// and filters routes by module requirements
export const getFilteredCommandBarRoutes = (tenant?: Tenant | null): SiteMapItem[] => {
  const allRoutes = commandBarRoutesFor(tenant);

  // Filter out dynamic routes with params like ":id" or catch-alls like ":slug*"
  const nonDynamicRoutes = allRoutes.filter(route => !route.route || !route.route.includes(":"));

  // Filter by module requirements - hide routes if tenant doesn't have required module
  const moduleFilteredRoutes = nonDynamicRoutes.filter(route => !tenant?.enabledModules || routeModulesEnabled(route, tenant.enabledModules));

  // Filter by tenant-specific visibility
  const tenantFilteredRoutes = moduleFilteredRoutes.filter(route => tenantMatchesAllowedSubDomains({
    tenantSubDomain: tenant?.subDomain,
    allowedSubDomains: route.tenantSubDomains,
    includeAllInDevelopment: false
  }) && (!route.sandboxOnly || !!tenant?.subDomain && isSandboxSubdomain(tenant.subDomain)));
  const routesByDestination = new Map<string, SiteMapItem>();
  for (const route of tenantFilteredRoutes) {
    const destination = route.canonicalRoute ?? route.route;
    if (!destination) {
      continue;
    }

    // A contextual alias carries a one-off message for the surface it sits on
    // (the main nav's "Analytics is moving" notice); the command bar opens the
    // destination itself, so it never navigates through the alias.
    const navigable = route.destinationKind === "contextual-alias" ? {
      ...route,
      route: destination
    } : route;
    const existing = routesByDestination.get(destination);
    if (!existing) {
      routesByDestination.set(destination, navigable);
      continue;
    }

    // The canonical entry owns the row; an alias only lends its search terms.
    const [base, alias] = existing.destinationKind === "contextual-alias" && navigable.destinationKind !== "contextual-alias" ? [navigable, existing] : [existing, navigable];
    const searchableAliasMetadata = [alias.name, alias.description, ...(alias.keywords ?? [])].filter((value): value is string => Boolean(value));
    routesByDestination.set(destination, {
      ...base,
      keywords: Array.from(new Set([...(base.keywords ?? []), ...searchableAliasMetadata]))
    });
  }

  // One result per destination, keeping every entry's searchable metadata.
  return Array.from(routesByDestination.values());
};
export const getRelativePath = (route: string): SiteMapItem[] => {
  // TODO: need to handle patient detail pages
  const findParents = (items: SiteMapItem[], targetRoute: string, parents: SiteMapItem[] = [], level: number = 0): {
    path: SiteMapItem[];
    level: number;
  }[] => {
    const results: {
      path: SiteMapItem[];
      level: number;
    }[] = [];
    for (const item of items) {
      // Always include the current item in the path if it has a name
      const currentPath = [...parents];
      if (item.name) {
        currentPath.push(item);
      }

      // Check if this is the target route (supports dynamic params)
      const matchesPrimary = item.route && matchesRoutePattern(item.route, targetRoute);
      const matchesAlias = item.routeAliases?.some(alias => matchesRoutePattern(alias, targetRoute));
      if (matchesPrimary || matchesAlias) {
        results.push({
          path: currentPath,
          level
        });
      }

      // If this item has children, search them with the current path
      if (item.children) {
        const childResults = findParents(item.children, targetRoute, currentPath, level + 1);
        results.push(...childResults);
      }
    }
    return results;
  };

  // Search every sitemap; a shared settings route's longer Settings path wins.
  const allResults = [providerSiteMap, analyticsConfig, settingsConfig, enterpriseSiteMap].flatMap(siteMap => findParents(siteMap, route));
  if (allResults.length === 0) {
    return [];
  }

  // Sort by path length (descending) to prefer more specific paths
  // If lengths are equal, prefer the lower level
  const winner = allResults.sort((a, b) => {
    const lengthDiff = b.path.length - a.path.length;
    if (lengthDiff !== 0) return lengthDiff;
    return a.level - b.level;
  })[0].path;

  // Collapse consecutive items that resolve to the same route. A child that is
  // literally the same page as its parent (e.g. the Patients section's redundant
  // "Patients" list row, which shares /patient) must not add a duplicate crumb.
  // Keeping the parent (first-wins) also keeps downstream consumers correct: the
  // breadcrumb's React key, and activeModule/MissingDataPage gating, which would
  // otherwise inherit the child's moduleNames.
  return winner.filter((item, i) => i === 0 || !item.route || item.route !== winner[i - 1].route);
};

/**
 * Index where a crumb trail's tail begins: the last crumb plus the run of
 * named-record crumbs (patient, care plan, note) leading into it. The tail says
 * what is on screen, so the breadcrumb bar keeps it when space runs out and
 * drops the section crumbs before it ("Clinical", "Notes") instead.
 */
export function getBreadcrumbTailStart(path: SiteMapItem[]): number {
  let start = path.length - 1;
  while (start > 0 && path[start - 1].dynamicNameKey) start--;
  return Math.max(start, 0);
}

/** For an any-of route, the module the tenant has, else the first listed. */
export function getCurrentModule(pathname: string, enabledModules?: readonly Module[]): Module | undefined {
  const routes = getAllCommandBarRoutes();
  const pathAfterTenant = pathname.substring(pathname.indexOf("/", pathname.indexOf("/") + 1));
  const currentPage = routes.find((route: SiteMapItem) => matchesRoutePattern(route.route, pathAfterTenant) || route.routeAliases?.some(alias => matchesRoutePattern(alias, pathAfterTenant)));
  if (currentPage?.moduleName) return currentPage.moduleName;
  return currentPage?.moduleNames?.find(module => enabledModules?.includes(module)) ?? currentPage?.moduleNames?.[0];
}

/**
 * Extract route parameters from a route pattern and actual path.
 * Returns an object with param names as keys and values from the path.
 */
export function extractRouteParams(pattern: string, path: string): Record<string, string> {
  const params: Record<string, string> = {};
  const patternSegments = pattern.split("/");
  const pathSegments = path.split("/");
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSeg = patternSegments[i];
    if (patternSeg.startsWith(":")) {
      const isCatchAll = patternSeg.endsWith("*");
      const paramName = patternSeg.replace(/^:/, "").replace(/\*$/, "");
      const value = isCatchAll ? pathSegments.slice(i).join("/") : pathSegments[i] || "";
      try {
        params[paramName] = decodeURIComponent(value);
      } catch {
        params[paramName] = value;
      }
      if (isCatchAll) break;
    }
  }
  return params;
}

/** Resolve the settings-engine record represented by a tenant-relative pathname. */
export function getCurrentSettingsResourceIdentity(pathname: string): CurrentSettingsResourceIdentity | null {
  const normalizedPath = pathname.split(/[?#]/, 1)[0].replace(/\/$/, "") || "/";
  const matches = getAllCommandBarRoutes().flatMap(item => {
    const patterns = [item.route, ...(item.routeAliases ?? [])].filter((route): route is string => Boolean(route));
    return patterns.filter(pattern => matchesRoutePattern(pattern, normalizedPath)).map(pattern => ({
      item,
      pattern,
      segmentCount: pattern.split("/").length,
      staticSegmentCount: pattern.split("/").filter(segment => segment && !segment.startsWith(":")).length
    }));
  });
  const match = matches.toSorted((a, b) => b.segmentCount - a.segmentCount || b.staticSegmentCount - a.staticSegmentCount)[0];
  const descriptor = match?.item.settingsResourceContext;
  if (!match || !descriptor || !isKnownSettingsTarget(descriptor.targetId)) {
    return null;
  }
  const params = extractRouteParams(match.pattern, normalizedPath);
  const recordId = params[descriptor.recordIdParam];
  if (!recordId) return null;
  return {
    targetId: descriptor.targetId,
    recordId,
    ...(descriptor.mode ? {
      mode: descriptor.mode
    } : {})
  };
}

/**
 * Replace route parameters in a pattern with actual values.
 * e.g. replaceRouteParams("/patient/:id", { id: "pat_123" }) -> "/patient/pat_123"
 */
export function replaceRouteParams(pattern: string, params: Record<string, string>): string {
  let result = pattern;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(new RegExp(`:${key}\\b`, "g"), value);
  }
  return result;
}